import React, { useState, useEffect } from 'react';
import { Button, Grid, TextField, FormControl, InputLabel, Select, MenuItem, Chip, Box } from '@mui/material';
import FilterSelectInput from '../../../sharedModules/form/components/FilterSelectInput';
import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowJobsFilterProps {
  includeStatus?: boolean;
  filterValues: {
    company_name_eq?: string;
    status_in?: string[];
    backflow_dates_scheduled_date_gteq?: string;
    backflow_dates_scheduled_date_lteq?: string;
    [key: string]: string | string[] | undefined;
  };
  setFilterValues: React.Dispatch<any>;
  onFilter: (filterValues: any) => void;
}

const BackflowJobsFilter: React.FC<BackflowJobsFilterProps> = (props) => {
  const { includeStatus = true, filterValues, setFilterValues, onFilter } = props;

  const [clientOptions, setClientOptions] = useState<any[]>([]);

  useEffect(() => {
    const fetchClientOptions = async () => {
      try {
        const response = await axios.get<string, any>('/backflows/backflow_job_groups/new');
        setClientOptions(response?.data?.client_options || []);
      } catch (error) {
        console.error('Error fetching client options:', error);
      }
    };

    fetchClientOptions();
  }, []);

  const handleFiltersKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') onFilter(filterValues);
  };

  const handleFilterChange = (key, value) => {
    let updatedFilterValues = { ...filterValues };

    updatedFilterValues[key] = value;
    setFilterValues(updatedFilterValues);
    if (['company_name_eq', 'status_in'].includes(key)) {
      onFilter(updatedFilterValues);
    }
  };

  const handleClear = () => {
    let updatedFilterValues = {
      backflow_dates_scheduled_date_gteq: '2000-01-01',
      backflow_dates_scheduled_date_lteq: '2100-01-01',
      company_name_eq: 'all',
      status_in: ['in_progress', 'not_started', 'overdue'],
    };

    setFilterValues(updatedFilterValues);
    onFilter(updatedFilterValues);
  };

  return (
    <Grid container spacing={1} alignItems="flex-start" onKeyDown={handleFiltersKeyPress}>
      <Grid item xs={6} md={2}>
        <FilterSelectInput
          value={filterValues?.company_name_eq}
          defaultChoice={{ label: 'All Customers', value: 'all' }}
          choices={clientOptions}
          valueChanged={(value) => handleFilterChange('company_name_eq', value)}
          label="Customer"
        />
      </Grid>
      {includeStatus && (
        <Grid item xs={12} md={3}>
          <FormControl variant="standard" style={{ width: '100%', marginTop: 8 }}>
            <InputLabel>Status</InputLabel>
            <Select
              multiple
              value={filterValues?.status_in || []}
              onChange={(e) => handleFilterChange('status_in', e.target.value)}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((value) => {
                    const statusLabels = {
                      not_started: 'Not Started',
                      overdue: 'Overdue',
                      closed_no_log: 'Closed - No Log',
                      in_progress: 'In Progress',
                      pending_approval: 'Pending Approval',
                      manual_review: 'Manual Review',
                      approved: 'Approved',
                      complete: 'Complete',
                      not_needed: 'Log Not Needed',
                    };
                    return <Chip key={value} label={statusLabels[value] || value} size="small" />;
                  })}
                </Box>
              )}
            >
              <MenuItem value="not_started">Not Started</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="closed_no_log">Closed - No Log</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="pending_approval">Pending Approval</MenuItem>
              <MenuItem value="manual_review">Manual Review</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="complete">Complete</MenuItem>
              <MenuItem value="not_needed">Log Not Needed</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={6} md={2}>
        <TextField
          label="Start Date"
          type="date"
          variant="standard"
          style={{ width: '100%', marginTop: 8 }}
          value={filterValues?.backflow_dates_scheduled_date_gteq}
          onChange={(e) => handleFilterChange('backflow_dates_scheduled_date_gteq', e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={6} md={2}>
        <TextField
          label="End Date"
          type="date"
          variant="standard"
          style={{ width: '100%', marginTop: 8 }}
          value={filterValues?.backflow_dates_scheduled_date_lteq}
          onChange={(e) => handleFilterChange('backflow_dates_scheduled_date_lteq', e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <Button onClick={() => onFilter(filterValues)} variant="contained" style={{ marginTop: 20 }}>
          Apply
        </Button>
        <Button onClick={handleClear} variant="contained" color="inherit" style={{ marginTop: 20, marginLeft: 7 }}>
          Clear
        </Button>
      </Grid>
    </Grid>
  );
};

export default BackflowJobsFilter;
